import type { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { GigType } from "model/Gig";
import { formatTimeRange } from "web-apps/company/components/schedule/utils/schedule.dates";
import { formatLocalDateWeekdayLabel } from "web-apps/company/utils/timeAndDate";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useScheduleDayShifts } from "web-apps/company/components/schedule/components/hooks/useScheduleDayShifts.hook";
import { useWorkerAvatar } from "web-apps/company/components/schedule/components/hooks/useWorkerAvatar";
import {
  groupScheduleDayShifts,
  type DayShift,
} from "./scheduleDayShifts.logic";
import {
  DayShiftsContent,
  EmptyState,
  PublicShiftAvatar,
  PublicShiftName,
  ShiftAvatar,
  ShiftAvatarImage,
  ShiftCard,
  ShiftDetails,
  ShiftName,
  ShiftRole,
  ShiftTime,
  TeamFilter,
  TeamFilters,
  TeamHeading,
  TeamSection,
  TeamShiftCount,
} from "./scheduleDayShiftsPanel.styled";

interface Props {
  readonly date: LocalDate | null;
  readonly onClose: () => void;
}

interface DayShiftAvatarProps {
  readonly avatarUrl: string | null;
  readonly isUnassigned: boolean;
}

const DayShiftAvatar = ({
  avatarUrl,
  isUnassigned,
}: DayShiftAvatarProps): JSX.Element => {
  if (isUnassigned) {
    return (
      <PublicShiftAvatar>
        <CustomIcon
          name="v2/warning-triangle"
          size="18px"
          color={ColorV2.NegativeRed}
        />
      </PublicShiftAvatar>
    );
  }
  return (
    <ShiftAvatar>
      {avatarUrl ? (
        <ShiftAvatarImage src={avatarUrl} alt="" />
      ) : (
        <CustomIcon name="v2/person" size="18px" color={ColorV2.Blue3} />
      )}
    </ShiftAvatar>
  );
};

interface DayShiftDetailsProps {
  readonly isUnassigned: boolean;
  readonly name: string;
  readonly role: string;
}

interface DayShiftCardProps {
  readonly shift: DayShift;
}

const DayShiftDetails = ({
  isUnassigned,
  name,
  role,
}: DayShiftDetailsProps): JSX.Element => (
  <ShiftDetails>
    {isUnassigned ? (
      <PublicShiftName>{name}</PublicShiftName>
    ) : (
      <ShiftName>{name}</ShiftName>
    )}
    {!isUnassigned && role && <ShiftRole>{role}</ShiftRole>}
  </ShiftDetails>
);

const DayShiftCard = ({ shift }: DayShiftCardProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const isPublicOrTeamShift =
    shift.gigType === GigType.PUBLIC ||
    shift.gigType === GigType.WORKPOOL ||
    shift.gigType === GigType.WORKGROUP;
  const isUnassigned = isPublicOrTeamShift && !shift.workerName;
  const avatarUrl = useWorkerAvatar({
    workerExternalId: shift.workerExternalId,
    isWorker: shift.isWorker && !isUnassigned,
  });
  const name =
    shift.workerName ??
    (isUnassigned ? t("Schedule.Unassigned") : t("Schedule.UnknownValue")) ??
    "";

  return (
    <ShiftCard>
      <ShiftTime>
        {formatTimeRange(shift.startTime, shift.endTime, i18n.language)}
      </ShiftTime>
      <DayShiftAvatar avatarUrl={avatarUrl} isUnassigned={isUnassigned} />
      <DayShiftDetails
        isUnassigned={isUnassigned}
        name={name}
        role={shift.role}
      />
    </ShiftCard>
  );
};

export const ScheduleDayShiftsPanel = ({
  date,
  onClose,
}: Props): JSX.Element | null => {
  const [companyState] = useCompanyStore();
  const { i18n, t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const dataState = useScheduleDayShifts({
    companyId: companyState.company?.id,
    date,
  });

  useEffect(() => {
    setSelectedTeamId(null);
  }, [date]);

  const teams = useMemo(
    () =>
      dataState.status === "ready"
        ? groupScheduleDayShifts({ rows: dataState.data.items })
        : [],
    [dataState],
  );
  const visibleTeams = useMemo(
    () =>
      selectedTeamId === null
        ? teams
        : teams.filter((team) => team.id === selectedTeamId),
    [selectedTeamId, teams],
  );
  const title = date
    ? `${formatLocalDateWeekdayLabel(date, i18n.language, "short")} ${date.dayOfMonth()}/${date.monthValue()}`
    : "";

  return (
    <SidePanel
      isOpen={date !== null}
      onClose={onClose}
      title={title}
      titleId="schedule-day-shifts-title"
    >
      <DayShiftsContent aria-busy={dataState.status === "loading"}>
        {dataState.status === "ready" && teams.length > 1 && (
          <TeamFilters aria-label={t("Schedule.FilterDayShifts")}>
            <TeamFilter
              type="button"
              $isSelected={selectedTeamId === null}
              aria-pressed={selectedTeamId === null}
              onClick={() => setSelectedTeamId(null)}
            >
              {t("Schedule.All")}
            </TeamFilter>
            {teams.map((team) => (
              <TeamFilter
                key={team.id}
                type="button"
                $isSelected={selectedTeamId === team.id}
                aria-pressed={selectedTeamId === team.id}
                onClick={() => setSelectedTeamId(team.id)}
              >
                {team.name}
              </TeamFilter>
            ))}
          </TeamFilters>
        )}
        {dataState.status === "loading" ? (
          <EmptyState>{t("Schedule.LoadingDayShifts")}</EmptyState>
        ) : dataState.status === "error" ? (
          <EmptyState>{t("Schedule.LoadDayShiftsError")}</EmptyState>
        ) : visibleTeams.length === 0 ? (
          <EmptyState>{t("Schedule.NoDayShifts")}</EmptyState>
        ) : (
          visibleTeams.map((team) => (
            <TeamSection key={team.id}>
              <TeamHeading>
                {team.name}{" "}
                <TeamShiftCount>
                  {t("Schedule.ShiftCount", { count: team.shifts.length })}
                </TeamShiftCount>
              </TeamHeading>
              {team.shifts.map((shift) => (
                <DayShiftCard key={shift.id} shift={shift} />
              ))}
            </TeamSection>
          ))
        )}
      </DayShiftsContent>
    </SidePanel>
  );
};
